import React, { useLayoutEffect, useRef, useState } from 'react';
import './TuIdea.css';
import ContactModal from './ContactModal';
import Footer from './Footer';
import NavBar from './NavBar';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Raiz from './raiz';

gsap.registerPlugin(ScrollTrigger);

function TuIdea() {
  const root = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useLayoutEffect(() => {
    // Respetar preferencias de accesibilidad
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const ctx = gsap.context(() => {
      // Configuración global para mejor rendimiento
      const defaultScrollConfig = {
        start: 'top 85%',
        toggleActions: 'play none none none',
      };

      // LOG para confirmar que el código está corriendo
      console.log('🎬 GSAP Animations Loading - Version 2.0');

      // Set initial states para elementos animados
      gsap.set('.diagonal-separator-tuidea', { scaleX: 0, transformOrigin: 'left center' });
      gsap.set('.section-header h2', { autoAlpha: 0, x: -50, rotateY: -20 });
      gsap.set('.section-header .header-line', { autoAlpha: 0, x: -50, rotateY: -20 });
      gsap.set('.process-step', { autoAlpha: 0, y: 80, scale: 0.8, rotateZ: -5 });
      gsap.set('.solution-item', { autoAlpha: 0, y: 60, scale: 0.85, rotateX: 20 });
      gsap.set('.section-branch-image-right', { autoAlpha: 0, x: 100, rotation: 10, scale: 0.8 });
      gsap.set('.cta-section .container > *', { autoAlpha: 0 });

      // ==================== HERO ====================
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      
      heroTl
        .fromTo(
          '.hero-branch-image-tuidea',
          { autoAlpha: 0, scale: 1.03 },
          { autoAlpha: 1, scale: 1, duration: 1.1, ease: 'power2.out' }
        )
        .fromTo(
          '.hero-content-tuidea > *',
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.12 },
          '-=0.6'
        );

      // ==================== SEPARADOR DIAGONAL ====================
      gsap.fromTo(
        '.diagonal-separator-tuidea',
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1,
          duration: 1.2,
          ease: 'expo.inOut',
          scrollTrigger: {
            trigger: '.process-section',
            ...defaultScrollConfig,
          },
        }
      );

      // ==================== HEADERS DE SECCIONES ====================
      gsap.utils.toArray('.section-header').forEach((header) => {
        gsap.fromTo(
          header.children,
          { autoAlpha: 0, x: -50, rotateY: -20 },
          {
            autoAlpha: 1,
            x: 0,
            rotateY: 0,
            duration: 1,
            ease: 'back.out(1.7)',
            stagger: 0.15,
            scrollTrigger: {
              trigger: header,
              ...defaultScrollConfig,
            },
          }
        );
      });

      // ==================== PROCESO ====================
      gsap.fromTo(
        '.process-step',
        { 
          autoAlpha: 0, 
          y: 80,
          scale: 0.8,
          rotateZ: -5
        },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          rotateZ: 0,
          duration: 1.2,
          ease: 'back.out(1.4)',
          stagger: {
            each: 0.15,
            from: 'start'
          },
          scrollTrigger: {
            trigger: '.process-grid',
            start: 'top 80%',
          },
        }
      );

      // ==================== SOLUCIONES (Batch) ====================
      ScrollTrigger.batch('.solution-item', {
        start: 'top 86%',
        onEnter: (batch) =>
          gsap.fromTo(
            batch,
            { 
              autoAlpha: 0, 
              y: 60,
              scale: 0.85,
              rotateX: 20
            },
            { 
              autoAlpha: 1, 
              y: 0,
              scale: 1,
              rotateX: 0, 
              duration: 1.1, 
              ease: 'back.out(1.5)', 
              stagger: {
                each: 0.12,
                grid: 'auto',
                from: 'start'
              }
            }
          ),
        once: true,
      });

      // ==================== IMAGEN DECORATIVA ====================
      gsap.fromTo(
        '.section-branch-image-right',
        { autoAlpha: 0, x: 100, rotation: 10, scale: 0.8 },
        {
          autoAlpha: 1,
          x: 0,
          rotation: 0,
          scale: 1,
          duration: 1.4,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: '.solutions-section',
            start: 'top 82%',
          },
        }
      );

      // ==================== CTA ====================
      const ctaTl = gsap.timeline({
        scrollTrigger: {
          trigger: '.cta-section',
          ...defaultScrollConfig,
        },
      });

      ctaTl
        .fromTo(
          '.cta-section .container > h3',
          { autoAlpha: 0, scale: 0.7, rotateX: -30 },
          {
            autoAlpha: 1,
            scale: 1,
            rotateX: 0,
            duration: 1,
            ease: 'back.out(2)',
          }
        )
        .fromTo(
          '.cta-section .container > .cta-line',
          { scaleX: 0 },
          { scaleX: 1, duration: 0.8, ease: 'expo.out' },
          '-=0.5'
        )
        .fromTo(
          '.cta-section .container > p',
          { autoAlpha: 0, y: 30 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          '-=0.4'
        )
        .fromTo(
          '.cta-button-large',
          { autoAlpha: 0, scale: 0.5, y: 40 },
          {
            autoAlpha: 1,
            scale: 1,
            y: 0,
            duration: 1,
            ease: 'elastic.out(1, 0.6)',
          },
          '-=0.3'
        );

    }, root);

    return () => {
      ctx.revert(); // Limpieza automática de animaciones y ScrollTriggers
    };
  }, []);

  return (
    <div className="TuIdea" ref={root}>
      <NavBar />

      {/* Hero Section */}
      <section className="tuidea-hero">
        <img
          src="/assets/fondosoftwaremed2.png"
          alt="Branch Ideas"
          className="hero-branch-image-tuidea"
        />
        <div className="hero-content-tuidea">
          <h1 className="hero-title-tuidea">Tu Idea, Nuestro Código</h1>
          <p className="hero-subtitle-tuidea">Transformamos tu visión en software real</p>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="diagonal-separator-tuidea"></div>
        <div className="container">
          <div className="section-header">
            <h2>Nuestro Proceso</h2>
            <div className="header-line"></div>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="step-number">01</div>
              <h4>Escuchamos</h4>
              <div className="step-line"></div>
              <p>Analizamos tus necesidades y objetivos de negocio</p>
            </div>

            <div className="process-step">
              <div className="step-number">02</div>
              <h4>Diseñamos</h4>
              <div className="step-line"></div>
              <p>Creamos una solución personalizada y escalable</p>
            </div>

            <div className="process-step">
              <div className="step-number">03</div>
              <h4>Desarrollamos</h4>
              <div className="step-line"></div>
              <p>Programamos tu software con las mejores prácticas</p>
            </div>

            <div className="process-step">
              <div className="step-number">04</div>
              <h4>Implementamos</h4>
              <div className="step-line"></div>
              <p>Ponemos en marcha tu solución con soporte completo</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="solutions-section">
        <img src="/assets/branch3.png" alt="Branch" className="section-branch-image-right" />
        <div className="container">
          <div className="section-header">
            <h2>Soluciones que Creamos</h2>
            <div className="header-line"></div>
          </div>

          <div className="solutions-grid">
            <div className="solution-item">
              <h4>Gestión Empresarial</h4>
              <div className="solution-line"></div>
              <p>ERP, CRM y sistemas de gestión a medida</p>
            </div>
            <div className="solution-item">
              <h4>E-commerce</h4>
              <div className="solution-line"></div>
              <p>Tiendas online y plataformas de venta</p>
            </div>
            <div className="solution-item">
              <h4>Aplicaciones Móviles</h4>
              <div className="solution-line"></div>
              <p>Apps para iOS y Android</p>
            </div>
            <div className="solution-item">
              <h4>Automatización</h4>
              <div className="solution-line"></div>
              <p>Software para optimizar procesos</p>
            </div>
            <div className="solution-item">
              <h4>Business Intelligence</h4>
              <div className="solution-line"></div>
              <p>Análisis de datos y reporting</p>
            </div>
            <div className="solution-item">
              <h4>Soluciones Cloud</h4>
              <div className="solution-line"></div>
              <p>Aplicaciones web escalables</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h3>¿Listo para empezar?</h3>
          <div className="cta-line"></div>
          <p>
            No importa si tu idea es grande o pequeña, simple o compleja. Estamos aquí para ayudarte a
            crear el software que tu negocio necesita.
          </p>                
        </div>
         <Raiz onClick={() => setIsModalOpen(true)} text='Contáctanos Ahora' />  
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <Footer />
    </div>
  );
}

export default TuIdea;